import { Fragment } from 'react'
import { Topbar, Footer, SEO } from '@components'

export const PrivacyPolicy = () => {
	return (
		<Fragment>
			<SEO
				title="Privacy Policy"
				description="How Pundis Botani Square collects, uses, and protects your personal data."
				path="/privacy-policy"
				noindex
			/>
			<Topbar />

			{/* Privacy Policy Content */}
			<section className="pt-24 pb-20 bg-gray-50">
				<div className="max-w-4xl mx-auto px-6">
					{/* Header */}
					<div className="text-center my-16">
						<h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
							Privacy Policy
						</h1>
						<div className="w-24 h-1 bg-primary mx-auto"></div>
					</div>

					{/* Summary Section */}
					<div className="bg-white rounded-xl shadow-lg p-8 mb-12">
						<div className="prose prose-lg max-w-none">
							<p className="text-lg text-gray-700 mb-6">
								We respect your privacy and are committed to protecting your
								data. Pundis Botani Square collects only the information
								necessary to provide and improve our services.
							</p>
							<p className="text-lg text-gray-700 mb-6">
								Your data is used to deliver app features such as rewards,
								promotions, and VIP services, and will never be shared with
								third parties without your consent.
							</p>
						</div>
					</div>

					{/* Full Privacy Policy */}
					<div className="bg-white rounded-xl shadow-lg p-8">
						<div className="prose prose-lg max-w-none">
							{/* Section I */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									I. Privacy Policy
								</h2>
								<p className="text-gray-700 mb-4">
									Pundis Botani Square ("us", "we", or "our") operates the
									Botani Square Mall website and mobile application (the
									"Service"). We respect the privacy of our users ("user" or
									"you") and are committed to protecting your personal
									information when you choose to use our Service.
								</p>
								<p className="text-gray-700 mb-4">
									This Privacy Policy explains our policies regarding the
									collection, use, and disclosure of personal data, as well as
									the choices you have in relation to that data.
								</p>
								<p className="text-gray-700">
									By using the Service, you agree to the collection and use of
									information in accordance with this policy. Unless otherwise
									defined, terms used in this Privacy Policy have the same
									meanings as in our Terms and Conditions.
								</p>
							</div>

							{/* Section II */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									II. Information Collection And Use
								</h2>
								<p className="text-gray-700 mb-4">
									We collect several types of information to provide and improve
									our Service. We only collect data that is necessary and
									relevant for delivering a better experience.
								</p>

								<h3 className="text-xl font-semibold text-gray-800 mb-3">
									Types of Data Collected
								</h3>

								<h4 className="text-lg font-semibold text-gray-800 mb-2">
									1. Personal Data
								</h4>
								<p className="text-gray-700 mb-3">
									While using our Service, we may ask you to provide personally
									identifiable information that can be used to contact or
									identify you. This may include, but is not limited to:
								</p>
								<ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
									<li>Email address</li>
									<li>First name and last name</li>
									<li>Phone number</li>
									<li>Address, Province, Postal Code, City</li>
									<li>Cookies and Usage Data</li>
								</ul>

								<h4 className="text-lg font-semibold text-gray-800 mb-2">
									2. Usage Data
								</h4>
								<p className="text-gray-700 mb-3">
									We may also collect information automatically whenever you
									access our Service, either through a browser or mobile device.
									This may include:
								</p>
								<ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
									<li>Internet Protocol (IP) address</li>
									<li>Browser type and version</li>
									<li>
										Pages visited, time and date of visit, and time spent on
										pages
									</li>
									<li>
										Device type, operating system, and unique device identifiers
									</li>
									<li>Diagnostic and performance data</li>
								</ul>

								<h4 className="text-lg font-semibold text-gray-800 mb-2">
									3. Tracking & Cookies Data
								</h4>
								<p className="text-gray-700 mb-3">
									We use cookies and similar tracking technologies to monitor
									activity on our Service and store certain information.
								</p>
								<p className="text-gray-700 mb-3">
									Examples of Cookies we use:
								</p>
								<ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
									<li>Session Cookies – to operate our Service</li>
									<li>
										Preference Cookies – to remember your settings and
										preferences
									</li>
									<li>Security Cookies – for security purposes</li>
								</ul>
								<p className="text-gray-700">
									You can choose to refuse cookies through your browser
									settings. However, some features of our Service may not
									function properly without them.
								</p>
							</div>

							{/* Section III */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									III. Use of Data
								</h2>
								<p className="text-gray-700 mb-3">
									Pundis Botani Square uses the collected data for the following
									purposes:
								</p>
								<ul className="list-disc list-inside text-gray-700 ml-4">
									<li>To provide and maintain the Service</li>
									<li>To notify you about changes to the Service</li>
									<li>
										To enable interactive features, when you choose to use them
									</li>
									<li>To provide customer care and support</li>
									<li>To conduct analysis and improve the Service</li>
									<li>To monitor usage of the Service</li>
									<li>To detect, prevent, and resolve technical issues</li>
								</ul>
							</div>

							{/* Section IV */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									IV. Transfer Of Data
								</h2>
								<ul className="list-disc list-inside text-gray-700 ml-4">
									<li className="mb-2">
										Your information, including Personal Data, may be
										transferred to and stored on servers outside of your
										province, country, or jurisdiction where data protection
										laws may differ.
									</li>
									<li className="mb-2">
										If you provide information from outside Indonesia, please
										note that we transfer and process your data in Indonesia. By
										using our Service, you consent to this transfer.
									</li>
									<li>
										We take all reasonable steps to ensure your data is treated
										securely and in accordance with this Privacy Policy, and
										that no transfer occurs without adequate data protection
										controls.
									</li>
								</ul>
							</div>

							{/* Section V */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									V. Disclosure Of Data
								</h2>
								<p className="text-gray-700 mb-3">
									Pundis Botani Square may disclose your Personal Data in good
									faith if such action is necessary to:
								</p>
								<ul className="list-disc list-inside text-gray-700 ml-4">
									<li>Comply with a legal obligation</li>
									<li>
										Protect and defend the rights or property of Pundis Botani
										Square
									</li>
									<li>
										Prevent or investigate possible wrongdoing in connection
										with the Service
									</li>
									<li>Protect the personal safety of users or the public</li>
									<li>Protect against legal liability</li>
								</ul>
							</div>

							{/* Section VI */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									VI. Security Of Data
								</h2>
								<p className="text-gray-700">
									The security of your data is important to us. However, please
									remember that no method of transmission over the internet or
									electronic storage is 100% secure. While we use commercially
									acceptable means to protect your Personal Data, we cannot
									guarantee absolute security.
								</p>
							</div>

							{/* Section VII */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									VII. Service Providers
								</h2>
								<p className="text-gray-700 mb-3">
									We may employ third-party companies and individuals to:
								</p>
								<ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
									<li>Facilitate our Service</li>
									<li>Provide the Service on our behalf</li>
									<li>Perform Service-related tasks</li>
									<li>Assist in analyzing usage of the Service</li>
								</ul>
								<p className="text-gray-700">
									These third parties will only have access to your Personal
									Data to perform tasks on our behalf and are obligated not to
									disclose or use it for any other purpose.
								</p>
							</div>

							{/* Section VIII */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									VIII. Links To Other Sites
								</h2>
								<ul className="list-disc list-inside text-gray-700 ml-4">
									<li className="mb-2">
										Our Service may contain links to third-party websites that
										are not operated by us. If you click a third-party link, you
										will be directed to that site. We strongly recommend
										reviewing the Privacy Policy of every site you visit.
									</li>
									<li>
										We have no control over, and assume no responsibility for,
										the content, policies, or practices of any third-party
										websites or services.
									</li>
								</ul>
							</div>

							{/* Section IX */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									IX. Children's Privacy
								</h2>
								<ul className="list-disc list-inside text-gray-700 ml-4">
									<li className="mb-2">
										Our Service does not address anyone under the age of 18
										("Children").
									</li>
									<li>
										We do not knowingly collect personally identifiable
										information from children. If you are a parent or guardian
										and you become aware that your child has provided us with
										Personal Data, please contact us immediately. If we discover
										that we have collected Personal Data from a child without
										parental consent, we will remove that data from our servers.
									</li>
								</ul>
							</div>

							{/* Section X */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									X. Changes To This Privacy Policy
								</h2>
								<ul className="list-disc list-inside text-gray-700 ml-4">
									<li className="mb-2">
										We may update our Privacy Policy from time to time. Any
										changes will be posted on this page with the updated
										effective date.
									</li>
									<li className="mb-2">
										We will notify you of significant changes via email and/or a
										prominent notice on our Service before the changes take
										effect.
									</li>
									<li>
										We encourage you to review this Privacy Policy periodically
										for updates.
									</li>
								</ul>
							</div>

							{/* Section XI */}
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-800 mb-4">
									XI. Contact Us
								</h2>
								<p className="text-gray-700 mb-4">
									If you have any questions about this Privacy Policy, please
									contact us:
								</p>
								<div className="space-y-2">
									<p className="text-gray-700">
										<strong>By email:</strong>{' '}
										<a
											href="mailto:crm@botanisquare.net"
											className="text-primary hover:text-primary-dark"
										>
											crm@botanisquare.net
										</a>
									</p>
									<p className="text-gray-700">
										<strong>By phone:</strong>{' '}
										<a
											href="tel:+6281277774798"
											className="text-primary hover:text-primary-dark"
										>
											+62 812 7777 4798
										</a>
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<Footer />
		</Fragment>
	)
}
